import React, { useState, useEffect, useMemo } from "react";
import { CircularProgress, Alert, Paper, InputAdornment, Chip, Tooltip } from "@mui/material";
import {
  Search,
  DirectionsCar,
  Park,
  LocationOn,
  CalendarToday,
  ErrorOutline,
  HourglassEmpty,
  Group,
  AccessTime,
  ExitToApp,
  CheckCircle,
  Star,
  StarBorder,
} from "@mui/icons-material";
import PropTypes from "prop-types";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import MDInput from "shared/components/MDInput";
import DataTable from "shared/components/Tables/DataTable";

import { getVehicleStatus, filterVipVehicles } from "../../model/vipCameModel";

// Función para obtener el color del estado
const getStatusColor = (status) => {
  switch (status) {
    case "EN VIP":
      return "warning";
    case "EN GENERAL":
      return "info";
    case "FUERA":
      return "error";
    default:
      return "default";
  }
};

function TableVipCame({
  groupVehicles = [],
  parkingVehicles = [],
  loading = false,
  error = null,
  groupName = "",
  onError = null,
  showNoData = { groupVehicles: false, parkingVehicles: false },
  isAllVehiclesMode = false,
  occupancyKnown = true,
}) {
  const [tabValue, setTabValue] = useState(isAllVehiclesMode ? 2 : 0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("TODOS");
  const filteredGroupVehicles = useMemo(
    () => filterVipVehicles(groupVehicles, searchQuery, statusFilter),
    [groupVehicles, searchQuery, statusFilter]
  );
  const filteredAllVehicles = filteredGroupVehicles;
  const filteredParkingVehicles = useMemo(
    () => filterVipVehicles(parkingVehicles, searchQuery, statusFilter),
    [parkingVehicles, searchQuery, statusFilter]
  );
  useEffect(() => {
    setTabValue(isAllVehiclesMode ? 2 : 0);
    setSearchQuery("");
    setStatusFilter("TODOS");
  }, [isAllVehiclesMode]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setStatusFilter("TODOS");
    setSearchQuery("");
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  // Columnas para vehículos del grupo
  const groupColumns = [
    { Header: "Placa", accessor: "plate", width: "12%" },
    { Header: "Grupo", accessor: "groupName", width: "12%" },
    { Header: "Ingreso General", accessor: "ent_gen", width: "15%" },
    { Header: "Salida General", accessor: "sal_gen", width: "15%" },
    { Header: "Ingreso VIP", accessor: "ent_vip", width: "15%" },
    { Header: "Salida VIP", accessor: "sal_vip", width: "15%" },
    { Header: "Estado", accessor: "status", width: "10%" },
  ];

  // Columnas para vehículos en parqueadero
  const parkingColumns = [
    { Header: "ID Vehículo", accessor: "idvehicle", width: "15%" },
    { Header: "Placa", accessor: "plate", width: "25%" },
    { Header: "Hora de Ingreso", accessor: "enterat", width: "30%" },
    { Header: "TID", accessor: "tid", width: "30%" },
  ];

  // Columnas para todos los vehículos
  const allVehiclesColumns = [
    { Header: "Placa", accessor: "plate", width: "12%" },
    { Header: "Grupo", accessor: "group", width: "12%" },
    { Header: "Ingreso General", accessor: "ent_gen", width: "15%" },
    { Header: "Salida General", accessor: "sal_gen", width: "15%" },
    { Header: "Ingreso VIP", accessor: "ent_vip", width: "15%" },
    { Header: "Salida VIP", accessor: "sal_vip", width: "15%" },
    { Header: "Estado", accessor: "status", width: "10%" },
  ];

  // Función para renderizar placa con ícono de zona
  const renderPlateWithZoneIcon = (plate, vehicle) => {
    const status = getVehicleStatus(vehicle);

    let icon = null;
    let tooltip = "";

    if (status === "EN VIP") {
      icon = <Star fontSize="small" sx={{ color: "#ffc107" }} />;
      tooltip = "Actualmente en zona VIP";
    } else if (status === "EN GENERAL") {
      icon = <StarBorder fontSize="small" sx={{ color: "#9e9e9e" }} />;
      tooltip = "Actualmente en zona General";
    } else if (status === "DENTRO") {
      icon = <DirectionsCar fontSize="small" sx={{ color: "#4caf50" }} />;
      tooltip = "Dentro del parqueadero";
    } else {
      icon = <DirectionsCar fontSize="small" sx={{ color: "#9e9e9e" }} />;
      tooltip =
        status === "SIN INFORMACIÓN"
          ? "No se pudo confirmar la ubicación"
          : "No está dentro del parqueadero";
    }

    return (
      <Tooltip title={tooltip} arrow>
        <MDBox display="flex" alignItems="center" gap={1}>
          {icon}
          <MDTypography
            variant="body1"
            fontWeight="bold"
            color="dark"
            sx={{
              fontSize: "0.875rem",
              fontFamily: "'Roboto Mono', monospace",
              letterSpacing: "0.5px",
            }}
          >
            {plate || "Sin placa"}
          </MDTypography>
        </MDBox>
      </Tooltip>
    );
  };

  // Función para renderizar placa simple
  const renderPlateSimple = (plate, isInParking) => (
    <MDBox display="flex" alignItems="center" gap={1}>
      <DirectionsCar
        fontSize="small"
        color={isInParking ? "success" : "disabled"}
        sx={{ color: isInParking ? "#4caf50" : "#9e9e9e" }}
      />
      <MDTypography
        variant="body1"
        fontWeight="bold"
        color="dark"
        sx={{
          fontSize: "0.875rem",
          fontFamily: "'Roboto Mono', monospace",
          letterSpacing: "0.5px",
        }}
      >
        {plate || "Sin placa"}
      </MDTypography>
    </MDBox>
  );

  // Función para renderizar celda de fecha/hora
  const renderDateTime = (dateTime, icon = null, emptyText = "---") => {
    if (!dateTime || dateTime === "N/A") {
      return (
        <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
          {emptyText}
        </MDTypography>
      );
    }
    return (
      <MDBox display="flex" alignItems="center" gap={1}>
        {icon || <AccessTime fontSize="small" color="action" />}
        <MDTypography variant="body2" fontWeight="medium" sx={{ fontSize: "0.8125rem" }}>
          {dateTime}
        </MDTypography>
      </MDBox>
    );
  };

  // Filas para vehículos del grupo
  const groupRows = filteredGroupVehicles.map((vehicle) => {
    const status = getVehicleStatus(vehicle);

    return {
      plate: renderPlateWithZoneIcon(vehicle.plate, vehicle),
      groupName: (
        <MDBox display="flex" alignItems="center" gap={1}>
          <LocationOn fontSize="small" color="primary" />
          <MDTypography
            variant="body1"
            fontWeight="bold"
            color="dark"
            sx={{ fontSize: "0.875rem" }}
          >
            {vehicle.groupName || "Sin grupo"}
          </MDTypography>
        </MDBox>
      ),
      ent_gen: renderDateTime(vehicle.ent_gen),
      sal_gen: renderDateTime(vehicle.sal_gen, <ExitToApp fontSize="small" color="action" />),
      ent_vip: renderDateTime(vehicle.ent_vip, <Star fontSize="small" sx={{ color: "#ffc107" }} />),
      sal_vip: renderDateTime(vehicle.sal_vip, <ExitToApp fontSize="small" color="action" />),
      status: (
        <Chip
          label={status}
          size="small"
          color={getStatusColor(status)}
          sx={{
            fontWeight: "bold",
            fontSize: "0.7rem",
            borderRadius: "4px",
            height: "26px",
          }}
        />
      ),
    };
  });

  // Filas para vehículos en parqueadero
  const parkingRows = filteredParkingVehicles.map((vehicle) => ({
    idvehicle: (
      <MDTypography
        variant="body2"
        fontWeight="medium"
        color="text.primary"
        sx={{ fontSize: "0.8125rem" }}
      >
        {vehicle.idvehicle || "N/A"}
      </MDTypography>
    ),
    plate: renderPlateSimple(vehicle.plate, true),
    enterat: renderDateTime(vehicle.enterat),
    tid: (
      <MDTypography
        variant="body2"
        color="text.secondary"
        fontWeight="medium"
        sx={{
          fontFamily: "'Roboto Mono', monospace",
          fontSize: "0.8125rem",
          letterSpacing: "0.5px",
        }}
      >
        {vehicle.tid}
      </MDTypography>
    ),
  }));

  // Filas para todos los vehículos
  const allVehiclesRows = filteredAllVehicles.map((vehicle) => {
    const status = getVehicleStatus(vehicle);

    return {
      plate: renderPlateWithZoneIcon(vehicle.plate, vehicle),
      group: (
        <MDBox display="flex" alignItems="center" gap={1}>
          <Group
            fontSize="small"
            color={
              vehicle.groupName && vehicle.groupName !== "Sin grupo asignado"
                ? "primary"
                : "disabled"
            }
          />
          <MDTypography
            variant="body2"
            fontWeight="medium"
            color={
              vehicle.groupName && vehicle.groupName !== "Sin grupo asignado"
                ? "text.primary"
                : "text.secondary"
            }
            sx={{ fontSize: "0.8125rem" }}
          >
            {vehicle.groupName || "Sin grupo asignado"}
          </MDTypography>
        </MDBox>
      ),
      ent_gen: renderDateTime(vehicle.ent_gen),
      sal_gen: renderDateTime(vehicle.sal_gen, <ExitToApp fontSize="small" color="action" />),
      ent_vip: renderDateTime(vehicle.ent_vip, <Star fontSize="small" sx={{ color: "#ffc107" }} />),
      sal_vip: renderDateTime(vehicle.sal_vip, <ExitToApp fontSize="small" color="action" />),
      status: (
        <Chip
          label={status}
          size="small"
          color={getStatusColor(status)}
          sx={{
            fontWeight: "bold",
            fontSize: "0.7rem",
            borderRadius: "4px",
            height: "26px",
          }}
        />
      ),
    };
  });

  const getSearchPlaceholder = () => {
    if (tabValue === 0) {
      return "Buscar placa, grupo, fechas...";
    } else if (tabValue === 1) {
      return "Buscar ID, placa, TID...";
    } else {
      return "Buscar placa, grupo, fechas...";
    }
  };

  const getSearchTitle = () => {
    if (tabValue === 0) {
      return `Vehículos del Grupo (${filteredGroupVehicles.length})`;
    } else if (tabValue === 1) {
      return `Vehículos en Parqueadero (${filteredParkingVehicles.length})`;
    } else {
      return `Todos los Vehículos (${filteredAllVehicles.length})`;
    }
  };

  const shouldShowLoading =
    loading && !showNoData.groupVehicles && !showNoData.parkingVehicles && !error;

  const hasAnyData = groupVehicles.length > 0 || parkingVehicles.length > 0;

  const shouldShowNoData =
    (showNoData.groupVehicles || showNoData.parkingVehicles) && !hasAnyData && !error;

  const shouldShowSearchAndTabs = hasAnyData || shouldShowNoData || error;

  const retryLoad = () => {
    if (onError && typeof onError === "function") {
      onError("reintentar");
    }
  };

  const presentCount = groupVehicles.filter((v) => v.isInside === true).length;
  const absentCount = groupVehicles.filter((v) => v.isInside === false).length;

  const vipCount = groupVehicles.filter((v) => getVehicleStatus(v) === "EN VIP").length;
  const generalCount = groupVehicles.filter((v) => getVehicleStatus(v) === "EN GENERAL").length;

  // Calcular estadísticas para el modo "Todos los Vehículos"
  const totalInside = groupVehicles.filter((v) => v.isInside === true).length;
  const totalOutside = groupVehicles.filter((v) => v.isInside === false).length;

  return (
    <>
      {shouldShowSearchAndTabs && (
        <Paper
          elevation={1}
          sx={{
            mb: 3,
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <MDBox
            p={2.5}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              backgroundColor: isAllVehiclesMode ? "#1a237e" : "primary.dark",
              background: isAllVehiclesMode
                ? "linear-gradient(135deg, #0d47a1 0%, #1a237e 100%)"
                : "linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)",
            }}
          >
            <MDBox>
              <MDTypography
                variant="h5"
                color="white"
                fontWeight="bold"
                sx={{
                  fontSize: "1.25rem",
                  letterSpacing: "0.3px",
                }}
              >
                {getSearchTitle()}
              </MDTypography>
              <MDTypography
                variant="body1"
                color="white"
                opacity={0.9}
                sx={{
                  fontSize: "0.875rem",
                  mt: 0.5,
                }}
              >
                {groupName || "Seleccione un modo de vista"}
                {!isAllVehiclesMode && tabValue === 0 && (
                  <span style={{ marginLeft: "10px" }}>
                    <Chip
                      label={
                        occupancyKnown ? `${presentCount} presentes` : "Ocupación sin información"
                      }
                      size="small"
                      color="success"
                      sx={{ ml: 1, mr: 1, fontSize: "0.7rem", height: "22px" }}
                    />
                    <Chip
                      label={occupancyKnown ? `${absentCount} ausentes` : "Fuera: sin información"}
                      size="small"
                      color="error"
                      sx={{ fontSize: "0.7rem", height: "22px" }}
                    />
                  </span>
                )}
                {!isAllVehiclesMode && tabValue === 0 && presentCount > 0 && (
                  <span style={{ marginLeft: "10px" }}>
                    <Chip
                      label={`${vipCount} VIP`}
                      size="small"
                      color="warning"
                      sx={{ mr: 1, fontSize: "0.7rem", height: "22px" }}
                    />
                    <Chip
                      label={`${generalCount} General`}
                      size="small"
                      color="info"
                      sx={{ fontSize: "0.7rem", height: "22px" }}
                    />
                  </span>
                )}
                {isAllVehiclesMode && tabValue === 2 && (
                  <span style={{ marginLeft: "10px" }}>
                    <Chip
                      label={occupancyKnown ? `${totalInside} dentro` : "Ocupación sin información"}
                      size="small"
                      color="success"
                      sx={{ ml: 1, mr: 1, fontSize: "0.7rem", height: "22px" }}
                    />
                    <Chip
                      label={occupancyKnown ? `${totalOutside} fuera` : "Fuera: sin información"}
                      size="small"
                      color="error"
                      sx={{ fontSize: "0.7rem", height: "22px" }}
                    />
                  </span>
                )}
              </MDTypography>
            </MDBox>

            {(hasAnyData || shouldShowNoData) && (
              <MDBox width="400px">
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    border: "2px solid",
                    borderColor: "rgba(255, 255, 255, 0.3)",
                    "&:hover": {
                      borderColor: isAllVehiclesMode ? "#7986cb" : "primary.light",
                      backgroundColor: "white",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <MDInput
                    fullWidth
                    placeholder={getSearchPlaceholder()}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search
                            fontSize="medium"
                            sx={{
                              color: isAllVehiclesMode ? "#7986cb" : "primary.main",
                              ml: 1,
                            }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <MDButton
                            size="small"
                            variant="text"
                            color={isAllVehiclesMode ? "secondary" : "primary"}
                            onClick={clearSearch}
                            aria-label="Limpiar búsqueda"
                            sx={{
                              minWidth: "auto",
                              p: 0.5,
                              mr: 0.5,
                              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                            }}
                          >
                            ✕
                          </MDButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        color: "text.primary",
                        py: 1.5,
                        fontSize: "0.9375rem",
                        fontWeight: 500,
                        "&::placeholder": {
                          color: "text.secondary",
                          opacity: 0.8,
                          fontSize: "0.875rem",
                        },
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                  />
                </Paper>
              </MDBox>
            )}
          </MDBox>

          {hasAnyData && (
            <MDBox p={2} display="flex" flexWrap="wrap" gap={1}>
              {[
                ["TODOS", "Todos"],
                ["EN VIP", "En VIP"],
                ["EN GENERAL", "En General"],
                ["FUERA", "Fuera"],
                ["SIN INFORMACIÓN", "Sin información"],
              ].map(([value, label]) => (
                <Chip
                  key={value}
                  label={label}
                  clickable
                  color={statusFilter === value ? "info" : "default"}
                  variant={statusFilter === value ? "filled" : "outlined"}
                  onClick={() => setStatusFilter(value)}
                />
              ))}
            </MDBox>
          )}
          {(hasAnyData || shouldShowNoData) && (
            <MDBox
              p={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                backgroundColor: "grey.50",
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <MDBox display="flex" alignItems="center" gap={3}>
                <MDBox display="flex" gap={1}>
                  {!isAllVehiclesMode && (
                    <>
                      <MDButton
                        variant={tabValue === 0 ? "contained" : "outlined"}
                        color="primary"
                        size="small"
                        onClick={() => handleTabChange(null, 0)}
                        startIcon={<DirectionsCar />}
                        sx={{
                          borderRadius: "6px",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.8125rem",
                          px: 2,
                          py: 1,
                        }}
                      >
                        Del Grupo ({filteredGroupVehicles.length})
                        {!searchQuery && occupancyKnown && (
                          <>
                            <Chip
                              label={presentCount}
                              size="small"
                              color="success"
                              sx={{ ml: 1, fontSize: "0.6rem", height: "18px" }}
                            />
                            <Chip
                              label={absentCount}
                              size="small"
                              color="error"
                              sx={{ ml: 0.5, fontSize: "0.6rem", height: "18px" }}
                            />
                          </>
                        )}
                      </MDButton>
                      <MDButton
                        variant={tabValue === 1 ? "contained" : "outlined"}
                        color="primary"
                        size="small"
                        onClick={() => handleTabChange(null, 1)}
                        startIcon={<Park />}
                        sx={{
                          borderRadius: "6px",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.8125rem",
                          px: 2,
                          py: 1,
                        }}
                      >
                        En Parqueadero (
                        {occupancyKnown ? filteredParkingVehicles.length : "sin información"})
                      </MDButton>
                    </>
                  )}
                  {isAllVehiclesMode && (
                    <MDButton
                      variant={tabValue === 2 ? "contained" : "outlined"}
                      color="secondary"
                      size="small"
                      onClick={() => handleTabChange(null, 2)}
                      startIcon={<Group />}
                      sx={{
                        borderRadius: "6px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.8125rem",
                        px: 2,
                        py: 1,
                      }}
                    >
                      Todos los Vehículos ({filteredAllVehicles.length})
                    </MDButton>
                  )}
                </MDBox>
              </MDBox>

              <MDBox>
                {searchQuery && (
                  <Chip
                    label={`${
                      tabValue === 0
                        ? filteredGroupVehicles.length
                        : tabValue === 1
                        ? filteredParkingVehicles.length
                        : filteredAllVehicles.length
                    } resultados`}
                    size="medium"
                    color={isAllVehiclesMode ? "secondary" : "info"}
                    variant="outlined"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  />
                )}
              </MDBox>
            </MDBox>
          )}
        </Paper>
      )}

      <MDBox pt={shouldShowSearchAndTabs ? 1 : 0}>
        {shouldShowLoading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <MDBox textAlign="center">
              <CircularProgress color={isAllVehiclesMode ? "secondary" : "primary"} size={60} />
              <MDTypography
                variant="h5"
                mt={2}
                color="text.primary"
                fontWeight="bold"
                sx={{ fontSize: "1.125rem" }}
              >
                {isAllVehiclesMode ? "Cargando todos los vehículos..." : "Cargando vehículos..."}
              </MDTypography>
              <MDTypography
                variant="body2"
                color="text.secondary"
                mt={1}
                sx={{ fontSize: "0.875rem" }}
              >
                {groupName ? `Obteniendo información de ${groupName}` : "Inicializando sistema"}
              </MDTypography>
            </MDBox>
          </MDBox>
        ) : error ? (
          <MDBox p={3}>
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: "8px",
                "& .MuiAlert-icon": { alignItems: "center" },
              }}
            >
              <MDTypography
                variant="h6"
                fontWeight="bold"
                color="text.primary"
                sx={{ fontSize: "1rem" }}
              >
                Error al cargar datos
              </MDTypography>
              <MDTypography
                variant="body1"
                color="text.primary"
                sx={{ fontSize: "0.875rem", mt: 0.5 }}
              >
                {error}
              </MDTypography>
            </Alert>
            <MDBox display="flex" justifyContent="center" mt={2}>
              <MDButton
                variant="contained"
                color={isAllVehiclesMode ? "secondary" : "primary"}
                onClick={retryLoad}
                disabled={loading}
                sx={{
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  px: 3,
                  py: 1,
                }}
              >
                {loading ? "Reintentando..." : "Reintentar carga"}
              </MDButton>
            </MDBox>
          </MDBox>
        ) : shouldShowNoData ? (
          <MDBox p={4} textAlign="center" bgcolor="background.paper" borderRadius="8px">
            <HourglassEmpty sx={{ fontSize: 60, color: "warning.main", mb: 2 }} />
            <MDTypography
              variant="h5"
              color="text.primary"
              fontWeight="bold"
              gutterBottom
              sx={{ fontSize: "1.125rem" }}
            >
              No se pudieron cargar los datos
            </MDTypography>
            <MDTypography
              variant="body1"
              color="text.secondary"
              mb={3}
              sx={{ fontSize: "0.875rem" }}
            >
              La carga de datos está tomando más tiempo de lo esperado. Esto puede deberse a
              problemas de conexión o carga del servidor.
            </MDTypography>
            <MDButton
              variant="contained"
              color={isAllVehiclesMode ? "secondary" : "primary"}
              onClick={retryLoad}
              sx={{
                fontWeight: 600,
                fontSize: "0.8125rem",
                px: 3,
              }}
            >
              Reintentar carga
            </MDButton>
          </MDBox>
        ) : (
          <>
            {tabValue === 0 ? (
              <>
                {filteredGroupVehicles.length === 0 ? (
                  <MDBox p={4} textAlign="center" bgcolor="background.paper" borderRadius="8px">
                    <DirectionsCar sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
                    <MDTypography
                      variant="h5"
                      color="text.primary"
                      fontWeight="bold"
                      gutterBottom
                      sx={{ fontSize: "1.125rem" }}
                    >
                      {!occupancyKnown
                        ? "Sin información de ocupación"
                        : searchQuery || statusFilter !== "TODOS"
                        ? "No se encontraron resultados"
                        : groupName
                        ? `No hay vehículos registrados en ${groupName}`
                        : "Seleccione un grupo"}
                    </MDTypography>
                    <MDTypography
                      variant="body1"
                      color="text.secondary"
                      mb={3}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      {!occupancyKnown
                        ? "Actualiza la consulta para confirmar qué vehículos están en el parqueadero."
                        : searchQuery || statusFilter !== "TODOS"
                        ? "No hay vehículos que coincidan con la búsqueda y el estado seleccionados."
                        : groupName
                        ? "Este grupo no tiene vehículos asignados."
                        : "Por favor, seleccione un grupo para ver sus vehículos."}
                    </MDTypography>
                    {searchQuery && (
                      <MDButton
                        variant="outlined"
                        color="primary"
                        onClick={clearSearch}
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.8125rem",
                        }}
                      >
                        Limpiar búsqueda
                      </MDButton>
                    )}
                  </MDBox>
                ) : (
                  <Paper elevation={0} sx={{ borderRadius: "8px", overflow: "hidden" }}>
                    <DataTable
                      table={{ columns: groupColumns, rows: groupRows }}
                      isSorted={false}
                      entriesPerPage={10}
                      showTotalEntries={true}
                      noEndBorder={true}
                      canSearch={false}
                      pagination={{ variant: "gradient", color: "primary" }}
                    />
                  </Paper>
                )}
              </>
            ) : tabValue === 1 ? (
              <>
                {filteredParkingVehicles.length === 0 ? (
                  <MDBox p={4} textAlign="center" bgcolor="background.paper" borderRadius="8px">
                    <Park sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
                    <MDTypography
                      variant="h5"
                      color="text.primary"
                      fontWeight="bold"
                      gutterBottom
                      sx={{ fontSize: "1.125rem" }}
                    >
                      {!occupancyKnown
                        ? "Sin información de ocupación"
                        : searchQuery || statusFilter !== "TODOS"
                        ? "No se encontraron resultados"
                        : groupName
                        ? `No hay vehículos en el parqueadero de ${groupName}`
                        : "Seleccione un grupo"}
                    </MDTypography>
                    <MDTypography
                      variant="body1"
                      color="text.secondary"
                      mb={3}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      {!occupancyKnown
                        ? "Actualiza la consulta para confirmar qué vehículos están en el parqueadero."
                        : searchQuery || statusFilter !== "TODOS"
                        ? "No hay vehículos que coincidan con la búsqueda y el estado seleccionados."
                        : groupName
                        ? "Actualmente no hay vehículos en el parqueadero de este grupo."
                        : "Por favor, seleccione un grupo para ver los vehículos en su parqueadero."}
                    </MDTypography>
                    {searchQuery && (
                      <MDButton
                        variant="outlined"
                        color="primary"
                        onClick={clearSearch}
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.8125rem",
                        }}
                      >
                        Limpiar búsqueda
                      </MDButton>
                    )}
                  </MDBox>
                ) : (
                  <Paper elevation={0} sx={{ borderRadius: "8px", overflow: "hidden" }}>
                    <DataTable
                      table={{ columns: parkingColumns, rows: parkingRows }}
                      isSorted={true}
                      entriesPerPage={10}
                      showTotalEntries={true}
                      noEndBorder={true}
                      canSearch={false}
                      pagination={{ variant: "gradient", color: "primary" }}
                    />
                  </Paper>
                )}
              </>
            ) : (
              // Tabla "Todos los Vehículos"
              <>
                {filteredAllVehicles.length === 0 ? (
                  <MDBox p={4} textAlign="center" bgcolor="background.paper" borderRadius="8px">
                    <Group sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
                    <MDTypography
                      variant="h5"
                      color="text.primary"
                      fontWeight="bold"
                      gutterBottom
                      sx={{ fontSize: "1.125rem" }}
                    >
                      {searchQuery || statusFilter !== "TODOS"
                        ? "No se encontraron resultados"
                        : "Todos los Vehículos"}
                    </MDTypography>
                    <MDTypography
                      variant="body1"
                      color="text.secondary"
                      mb={3}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      {!occupancyKnown
                        ? "Actualiza la consulta para confirmar qué vehículos están en el parqueadero."
                        : searchQuery || statusFilter !== "TODOS"
                        ? "No hay vehículos que coincidan con la búsqueda y el estado seleccionados."
                        : "No hay vehículos registrados en el sistema."}
                    </MDTypography>
                    {searchQuery && (
                      <MDButton
                        variant="outlined"
                        color="secondary"
                        onClick={clearSearch}
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.8125rem",
                        }}
                      >
                        Limpiar búsqueda
                      </MDButton>
                    )}
                  </MDBox>
                ) : (
                  <Paper elevation={0} sx={{ borderRadius: "8px", overflow: "hidden" }}>
                    <DataTable
                      table={{ columns: allVehiclesColumns, rows: allVehiclesRows }}
                      isSorted={false}
                      entriesPerPage={15}
                      showTotalEntries={true}
                      noEndBorder={true}
                      canSearch={false}
                      pagination={{ variant: "gradient", color: "secondary" }}
                    />
                  </Paper>
                )}
              </>
            )}
          </>
        )}
      </MDBox>
    </>
  );
}

TableVipCame.propTypes = {
  groupVehicles: PropTypes.array,
  parkingVehicles: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  groupName: PropTypes.string,
  onError: PropTypes.func,
  showNoData: PropTypes.shape({
    groupVehicles: PropTypes.bool,
    parkingVehicles: PropTypes.bool,
  }),
  isAllVehiclesMode: PropTypes.bool,
  occupancyKnown: PropTypes.bool,
};

TableVipCame.defaultProps = {
  groupVehicles: [],
  parkingVehicles: [],
  loading: false,
  error: null,
  groupName: "",
  onError: null,
  showNoData: { groupVehicles: false, parkingVehicles: false },
  isAllVehiclesMode: false,
};

export default TableVipCame;
